// src/pages/Dashboard/Admin/Requests/RequestsDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useRequestsDashboard } from "./hooks/useRequestsDashboard";

import DashboardRequestsHeader from "./components/DashboardRequestsHeader";
import RequestsKpiCards from "./components/RequestsKpiCards";
import RequestsFiltersCard from "./components/RequestsFiltersCard";
import RequestsTable from "./components/RequestsTable";
import RequestFormDrawer from "./components/RequestFormDrawer";
import RequestDetailsDrawer from "./components/RequestDetailsDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedRequestsPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Requêtes support
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function RequestsDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const dashboard = useRequestsDashboard();

  if (roleId !== 1) {
    return <UnauthorizedRequestsPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardRequestsHeader
          loading={dashboard.loading}
          onCreate={dashboard.openCreateDrawer}
          onRefresh={dashboard.fetchRequests}
        />

        <RequestsKpiCards
          stats={dashboard.stats}
          filteredStats={dashboard.filteredStats}
          loading={dashboard.loading}
        />

        <RequestsFiltersCard
          filters={dashboard.filters}
          setFilter={dashboard.setFilter}
          resetFilters={dashboard.resetFilters}
          filteredCount={dashboard.filteredRequests.length}
          totalCount={dashboard.requests.length}
          loading={dashboard.loading}
          onRefresh={dashboard.fetchRequests}
        />

        <RequestsTable
          requests={dashboard.filteredRequests}
          loading={dashboard.loading}
          deletingId={dashboard.deletingId}
          onEdit={dashboard.openEditDrawer}
          onDelete={dashboard.handleDeleteRequest}
          onOpenDetails={dashboard.openDetailsDrawer}
        />
      </div>

      <RequestFormDrawer
        isOpen={dashboard.formDrawerOpen}
        mode={dashboard.formMode}
        request={dashboard.editableRequest}
        submitting={dashboard.submittingForm}
        onClose={dashboard.closeFormDrawer}
        onSubmit={
          dashboard.formMode === "create"
            ? dashboard.handleCreateRequest
            : dashboard.handleUpdateRequest
        }
      />

      <RequestDetailsDrawer
        isOpen={dashboard.detailsDrawerOpen}
        request={dashboard.selectedRequest}
        deletingId={dashboard.deletingId}
        onClose={dashboard.closeDetailsDrawer}
        onEdit={(request) => {
          dashboard.closeDetailsDrawer();
          dashboard.openEditDrawer(request);
        }}
        onDelete={dashboard.handleDeleteRequest}
      />

      <ToastContainer />
    </section>
  );
}